import { create } from 'zustand'
import { lineDistanceMeters, nearestPointOnLine, type LngLat, type LngLatEle } from '../lib/geo'
import type { Instruction, Sport } from '../lib/models'
import { friendlyGeoError, keepScreenOn, speak, setVoiceEnabled } from '../lib/platform'
import { computeRoute } from '../lib/routing'

const OFF_ROUTE_M = 60
const OFF_ROUTE_FIXES = 3
const ANNOUNCE_AHEAD_M = 90

interface NavState {
  active: boolean
  tourName: string | null
  sport: Sport
  coords: LngLatEle[]
  instructions: Instruction[]
  totalM: number
  position: GeolocationPosition | null
  /** snapped progress along the route */
  alongM: number
  distanceToRouteM: number
  offRoute: boolean
  rerouting: boolean
  voice: boolean
  gpsError: string | null
  start: (name: string, coords: LngLatEle[], instructions: Instruction[], sport: Sport) => void
  stop: () => void
  setVoice: (on: boolean) => void
}

let watchId: number | null = null
let offRouteCount = 0
let announcedAtM = -1
let rerouteBusy = false

export const useNav = create<NavState>((set, get) => {
  const onFix = async (pos: GeolocationPosition) => {
    const s = get()
    if (!s.active) return
    const here: LngLat = [pos.coords.longitude, pos.coords.latitude]
    const near = nearestPointOnLine(s.coords, here)
    const update: Partial<NavState> = {
      position: pos,
      alongM: near.alongM,
      distanceToRouteM: near.distM,
      gpsError: null,
    }

    if (near.distM > OFF_ROUTE_M) {
      offRouteCount++
      if (offRouteCount === OFF_ROUTE_FIXES) {
        update.offRoute = true
        speak('You are off route. Recalculating.')
        if (!rerouteBusy) {
          rerouteBusy = true
          set({ rerouting: true })
          try {
            // re-route from here to the final destination
            const dest = s.coords[s.coords.length - 1]
            const route = await computeRoute([here, [dest[0], dest[1]]], s.sport)
            offRouteCount = 0
            announcedAtM = -1
            set({
              coords: route.coords,
              instructions: route.instructions,
              totalM: route.distanceM,
              offRoute: false,
              rerouting: false,
            })
          } catch {
            set({ rerouting: false }) // keep old line; user finds their way back
          } finally {
            rerouteBusy = false
          }
        }
      }
    } else {
      offRouteCount = 0
      update.offRoute = false
    }

    // announce the next instruction when close
    const next = s.instructions.find((i) => i.atM > near.alongM + 8)
    if (next && next.atM - near.alongM < ANNOUNCE_AHEAD_M && announcedAtM !== next.atM) {
      announcedAtM = next.atM
      speak(next.text)
    }
    set(update)
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && get().active) startWatch()
  })

  const startWatch = () => {
    if (watchId != null) navigator.geolocation.clearWatch(watchId)
    watchId = navigator.geolocation.watchPosition(
      (p) => void onFix(p),
      (e) => set({ gpsError: friendlyGeoError(e.code) }),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 30000 },
    )
  }

  return {
    active: false,
    tourName: null,
    sport: 'hike',
    coords: [],
    instructions: [],
    totalM: 0,
    position: null,
    alongM: 0,
    distanceToRouteM: 0,
    offRoute: false,
    rerouting: false,
    voice: true,
    gpsError: null,

    start: (tourName, coords, instructions, sport) => {
      offRouteCount = 0
      announcedAtM = -1
      set({
        active: true,
        tourName,
        coords,
        instructions,
        sport,
        totalM: lineDistanceMeters(coords),
        alongM: 0,
        offRoute: false,
        rerouting: false,
        position: null,
        gpsError: null,
      })
      setVoiceEnabled(get().voice)
      keepScreenOn(true)
      startWatch()
      speak(`Navigation started. ${Math.round(lineDistanceMeters(coords) / 100) / 10} kilometers to go.`)
    },

    stop: () => {
      if (watchId != null) navigator.geolocation.clearWatch(watchId)
      watchId = null
      keepScreenOn(false)
      setVoiceEnabled(false)
      set({ active: false, tourName: null, coords: [], instructions: [], position: null })
    },

    setVoice: (voice) => {
      set({ voice })
      setVoiceEnabled(voice && get().active)
    },
  }
})

/** Next upcoming instruction and the distance to it. */
export function nextInstruction(s: NavState): { instruction: Instruction; inM: number } | null {
  const next = s.instructions.find((i) => i.atM > s.alongM + 8)
  return next ? { instruction: next, inM: Math.max(0, next.atM - s.alongM) } : null
}
